import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { Button, Chip, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  ArchiveRounded as ArchiveIcon,
  BlockRounded as BlockIcon,
  CheckRounded as CheckIcon,
  DoneAllRounded as DoneAllIcon,
  EditRounded as EditIcon,
  PrintRounded as PrintIcon,
} from '@material-ui/icons';

import OrderStatusChip from '../Chips/OrderStatusChip.component';
import AdvStatusChip from '../../../components/Admin/Order/Chip/AdvStatusChip';
import { Order } from '../../types/order.type';
import { ORDER_ADV_STATUSES, OrderAdvStatus } from '../../types/order-adv-status.type';
import { ADMIN_ORDER_APP_PATHS } from '../../constants/orders-app-paths.constant';
import { formatPrice } from '../../../shared/utils/format-price.util';
import pluralFormatter from '../../../shared/utils/plural-formater';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  title: {
    '& > h2': {
      margin: 0,
    },
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  dates: {
    fontSize: '0.875rem',
    color: '#616161',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',

    '& .MuiButton-root': {
      textTransform: 'none',
    },
  },
  advButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  cancelButton: {
    borderColor: '#f42929',
    color: '#f42929',
  },
  neutralChip: {
    borderRadius: 4,
    backgroundColor: '#e0e0e0',
    color: '#424242',
    fontWeight: 600,
    fontSize: 12,
  },
}));

interface AdvAction {
  label: string;
  nextStatus: OrderAdvStatus;
  icon: JSX.Element;
}

const getAdvAction = (advStatus: OrderAdvStatus): AdvAction | null => {
  switch (advStatus) {
    case ORDER_ADV_STATUSES.INPUT:
    case ORDER_ADV_STATUSES.CANCELED:
      return { label: 'Valider', nextStatus: ORDER_ADV_STATUSES.VALIDATE, icon: <CheckIcon /> };
    case ORDER_ADV_STATUSES.VALIDATE:
      return { label: 'Traiter', nextStatus: ORDER_ADV_STATUSES.PROCESSED, icon: <DoneAllIcon /> };
    case ORDER_ADV_STATUSES.PROCESSED:
      return { label: 'Archiver', nextStatus: ORDER_ADV_STATUSES.ARCHIVED, icon: <ArchiveIcon /> };
    default:
      return null;
  }
};

interface Props {
  order: Order;
  isMutating: boolean;
  onAdvStatusChange: (nextStatus: OrderAdvStatus) => void;
  onPrintLabels: () => void;
  onArchiveAndPrint: () => void;
}

export default function OrderDetailsHeader({
  order,
  isMutating,
  onAdvStatusChange,
  onPrintLabels,
  onArchiveAndPrint,
}: Props) {
  const classes = useStyles();
  const history = useHistory();

  const advAction = getAdvAction(order.advStatus);
  const isArchived = order.advStatus === ORDER_ADV_STATUSES.ARCHIVED;

  const productCount = order.items.reduce((total, item) => total + item.quantity, 0);
  const productCountLabel = `${productCount} ${pluralFormatter({
    count: productCount,
    singular: 'produit',
    plural: 'produits',
  })}`;

  return (
    <header className={classes.root}>
      <div className={classes.identity}>
        <div className={classes.title}>
          <h2>Commande n° {order.id}</h2>
          <span className={classes.dates}>
            Création le {moment(order.createdAt).format('DD/MM/YYYY [à] HH:mm')} - Livraison le{' '}
            {moment(order.deliveryInfos.deliveryDate).format('DD/MM/YYYY')}
          </span>
        </div>
        <div className={classes.statusRow}>
          <OrderStatusChip status={order.status} size="small" />
          <AdvStatusChip advStatus={order.advStatus} />
          <Divider orientation="vertical" flexItem />
          <Chip className={classes.neutralChip} size="small" label={productCountLabel} />
          <Chip className={classes.neutralChip} size="small" label={`${formatPrice(order.totals.totalTtc)} TTC`} />
        </div>
      </div>

      <div className={classes.actions}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => history.push(ADMIN_ORDER_APP_PATHS.ORDER_UPDATE(order.id))}
        >
          Modifier
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<PrintIcon />}
          disabled={isMutating}
          onClick={isArchived ? onPrintLabels : onArchiveAndPrint}
        >
          {isArchived ? 'Étiquettes' : 'Archiver et imprimer'}
        </Button>

        {advAction && (
          <Button
            size="small"
            variant="contained"
            className={classes.advButton}
            startIcon={advAction.icon}
            disabled={isMutating}
            onClick={() => onAdvStatusChange(advAction.nextStatus)}
          >
            {advAction.label}
          </Button>
        )}

        {isArchived && (
          <Button
            size="small"
            variant="outlined"
            className={classes.cancelButton}
            startIcon={<BlockIcon />}
            disabled={isMutating}
            onClick={() => onAdvStatusChange(ORDER_ADV_STATUSES.CANCELED)}
          >
            Annuler
          </Button>
        )}
      </div>
    </header>
  );
}
